import type { RefObject } from "react";
import React, { useMemo } from "react";
import { Box, Center, Heading, Stack, useBreakpointValue } from "@chakra-ui/react";
import { BarometerChart } from "screens/landing/tabs/collections/chartComponents/BarometerChart";
import QuadrantChart from "screens/landing/tabs/collections/chartComponents/QuadrantChart/QuadrantChart";
import { CustomVerticalBarChart, getScoreColor } from "screens/landing/tabs/collections/chartComponents/CustomVerticalBarChart";
import type { DealFinderTicker } from "../../../../api/tickers/models/DealFinderTicker";
import {
  createRatingConfig,
  getRatingInfo,
  useBuySellChartColors,
} from "screens/landing/tabs/collections/chartComponents/data/buySellData";
import { useDashboardMetricsData } from "hooks";
import { Array } from "runtypes";
import { Score } from "../../../../api/tickers/models/Score";
import { BlurWrapper } from "screens/common/components/BlurWrapper";

// Mock data for charts when useMockData is true
const MOCK_BUY_SELL_SCORE = 4.2;
const MOCK_BUY_SELL_PERCENTAGE = (MOCK_BUY_SELL_SCORE / 5) * 100;

const MOCK_SCORES = {
  Market: { value: 5 },
  Product: { value: 7 },
  Financials: { value: 9 },
  Management: { value: 5 },
  Traction: { value: 2 },
  Moat: { value: 3 },
  Potential: { value: 8 },
};

interface PurchaseDealLeftSectionProps {
  deal: DealFinderTicker;
  leftColumnRef: RefObject<HTMLDivElement>;
  leftScrollbarStyle: any;
  borderColor: string;
}

export const PurchaseDealLeftSection: React.FC<PurchaseDealLeftSectionProps> = ({
  deal,
  leftColumnRef,
  leftScrollbarStyle,
  borderColor,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const chartColors = useBuySellChartColors();
  const ratingConfig = useMemo(() => createRatingConfig(chartColors), [chartColors]);

  const MOCK_QUADRANT_DATA = useMemo(
    () => ({
      investmentScoreAnalytical: { value: "0" },
      investmentScoreSentiment: { value: "0" },
      projectName: deal.name,
      companyLogoUrl: deal.companyLogoUrl || "",
    }),
    [deal]
  );

  const buySellScore = !deal.indicators.buy_sell.value
    ? MOCK_BUY_SELL_SCORE
    : deal.indicators.buy_sell.value && deal.indicators.buy_sell.value <= 5
    ? deal.indicators.buy_sell.value
    : null;

  const buySellScorePercentage = !deal.indicators.buy_sell.value ? MOCK_BUY_SELL_PERCENTAGE : buySellScore ? (buySellScore / 5) * 100 : 0;

  const areScoreValuesNull = useMemo(() => {
    return deal.scores && Object.values(deal.scores).every((item) => item.value === null);
  }, [deal.scores]);

  const singleProjectData = useMemo(() => {
    if (!deal.investmentScoreAnalytical && !deal.investmentScoreSentiment) {
      return MOCK_QUADRANT_DATA;
    }
    return {
      investmentScoreAnalytical: deal.investmentScoreAnalytical ? { value: deal.investmentScoreAnalytical.toString() } : undefined,
      investmentScoreSentiment: deal.investmentScoreSentiment ? { value: deal.investmentScoreSentiment.toString() } : undefined,
      projectName: deal.name,
      id: deal.id,
      companyLogoUrl: deal.companyLogoUrl || "",
    };
  }, [MOCK_QUADRANT_DATA, deal]);
  const { quadrantData } = useDashboardMetricsData("pi_quadrant_index", undefined, singleProjectData);

  return (
    <Stack
      width="100%"
      height={"100%"}
      spacing={0}
      direction={{ base: "column", lg: "column" }}
      justifyContent={isMobile ? "space-between" : "flex-start"}
      py="1.5rem"
      pl={{ base: "1.5rem", md: "1.5rem", lg: "1.5rem" }}
      pr={{ base: "1.5rem", md: ".75rem", lg: ".75rem" }}
      overflowY="auto"
      overflowX="hidden"
      css={leftScrollbarStyle}
      ref={leftColumnRef}>
      <Box pb="3rem" width="100%">
        <Heading size="md" pb={"1rem"}>
          Trading Analysis
        </Heading>
        <BarometerChart
          blurContent
          bottomMarker={{
            label: `${buySellScore != null ? getRatingInfo(buySellScore, ratingConfig).label : ""} (${
              buySellScore != null ? buySellScore.toFixed(1) : ""
            })`,
            value: buySellScorePercentage,
          }}
        />
      </Box>
      <Box p="0" width="100%">
        <Heading size="md" mb={3}>
          Pi Quadrant Index
        </Heading>
        <Box height={isMobile ? "15rem" : "19rem"}>
          <Center width="100%" height="100%">
            <QuadrantChart blurContent data={quadrantData} showProjectAnalysisPopover={false} />
          </Center>
        </Box>
      </Box>
      {deal.scores && (
        <Box mt={{ base: "0.5rem", md: "2rem", lg: "2rem" }}>
          <Heading size="md" mb={3}>
            Company Scorecard
          </Heading>
          <Box width="100%" border="1px" borderColor={borderColor} borderRadius="md" p="2px">
            <BlurWrapper blurContent blurStyle={{ left: "119px" }} width="100%">
              <CustomVerticalBarChart
                data={
                  areScoreValuesNull
                    ? Object.entries(MOCK_SCORES).map(([scoreType, score]) => ({
                        label: scoreType.charAt(0).toUpperCase() + scoreType.slice(1).replace(/([A-Z])/g, " $1"),
                        value: score.value,
                        color: getScoreColor(score.value),
                      }))
                    : Array(Score).guard(deal.scores)
                    ? deal.scores.map((score) => {
                        const scoreValue = score.value !== null ? score.value : 0;

                        return {
                          label: score.label,
                          value: scoreValue,
                          color: getScoreColor(scoreValue),
                        };
                      })
                    : Object.entries(deal.scores || {}).map(([scoreType, score]) => {
                        const scoreValue = score.value !== null ? score.value : 0;
                        return {
                          label: scoreType.charAt(0).toUpperCase() + scoreType.slice(1).replace(/([A-Z])/g, " $1"),
                          value: scoreValue,
                          color: getScoreColor(scoreValue),
                        };
                      })
                }
                yAxisType="category"
                chartId="dealFinderScoresBarChart"
                height="18rem"
                chartHeight={"100%"}
              />
            </BlurWrapper>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
